import { Text } from '@thanx/uikit/text'
import type { AvailableReward } from 'api/recoveryAi.types'
import ChevronRight from 'components/Icons/ChevronRight'
import Sparkle from 'components/Icons/Sparkle'
import RewardImage from 'components/RewardPreviewCard/RewardImage'
import { buildTranslate } from 'locales'
import React from 'react'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { currencyFormatter } from 'utilities/formatters'
import { useMerchantBrand } from './useMerchantBrand'

const t = buildTranslate('recovery_ai.sandbox.reward_card')

const IMAGE_SIZE_PX = 56
const CENTS_PER_DOLLAR = 100

// Chat-variant (image-only) image treatment. Aspect ratio matches the shared
// RichReward image treatment (components/RewardPreviewCard/RichReward).
// Exported so it is unit-assertable: styletron's css() is a no-op under
// renderWithStore (no StyletronProvider), so it cannot be read back off the
// rendered DOM, the same reason POINTS_BADGE_STYLE used to be exported here.
export const CHAT_IMAGE_STYLE: StyleObject = {
  display: 'block',
  width: '100%',
  aspectRatio: '15/7',
  objectFit: 'cover',
  borderRadius: '8px',
}

// Fallback (no-image) chat card — mirrors ordering-ui's RecoveryChat/ChatOfferCard.
const CHAT_FALLBACK_PADDING_PX = 16
const CHAT_FALLBACK_GAP_PX = 12
const CHAT_FALLBACK_BORDER_RADIUS_PX = 4

type Props = {
  reward: AvailableReward
  /**
   * Renders a NON-INTERACTIVE "View your reward ›" affordance beneath the card
   * (static demo only). Default false.
   */
  showRewardLink?: boolean
  /**
   * 'catalog' (default) is the existing thumb + label + value + points row,
   * used by the right-column approved-rewards grid (SimulatorEngine). 'chat'
   * is the in-transcript treatment (AI-1197): an image-only banner when the
   * reward has one, else the same neutral outlined fallback the consumer
   * ordering-ui ChatOfferCard shows. Default keeps every existing call site
   * byte-identical.
   */
  variant?: 'catalog' | 'chat'
}

// Rich reward preview (AI-164): reward visual + label + value framing + points,
// reading nexus #565's enriched reward fields (image_url / value_cents /
// points). AI-585: the visual ALWAYS renders via the shared RewardImage (offer
// image → merchant reward icon → brand-colored default symbol); only the value
// and points lines stay conditional on their fields.
// Named SandboxRewardCard (not RewardPreviewCard) to disambiguate from the
// shared components/RewardPreviewCard whose RewardImage it composes.
const SandboxRewardCard = ({
  reward,
  showRewardLink = false,
  variant = 'catalog',
}: Props) => {
  const [css] = useStyletron()
  // Called unconditionally (Rules of Hooks) even when the link is off. It is a
  // useMemo over useCurrentMerchant — the same Redux read the composed
  // RewardImage already performs — so it adds no fetch.
  const { color: brandColor } = useMerchantBrand()

  // Chat-only: prefer the customer-facing text (template description, as the
  // consumer app renders it) over the merchant-internal `label`, since the
  // chat transcript is a preview of what the CUSTOMER sees. Falsy-chained
  // (not `??`) so an empty-string customer_label also falls through to
  // label.
  const chatRewardName =
    reward.customer_label || reward.label || t('reward_fallback_name')

  const chatFallbackCard = (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: `${CHAT_FALLBACK_GAP_PX}px`,
        padding: `${CHAT_FALLBACK_PADDING_PX}px`,
        width: '100%',
        borderRadius: `${CHAT_FALLBACK_BORDER_RADIUS_PX}px`,
        border: `1px solid ${merchantTheme.colors.grey30}`,
        backgroundColor: merchantTheme.colors.white,
      })}
    >
      <Sparkle fillColor={brandColor} />
      <Text.BodyText4 color="grey90">{chatRewardName}</Text.BodyText4>
    </div>
  )

  const catalogCard = (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: 'white',
        border: `1px solid ${merchantTheme.colors.grey20}`,
        borderRadius: '8px',
        padding: '12px',
      })}
    >
      {/* Reward visual, like offers everywhere else (AI-585): offer image if
          present → merchant reward icon → brand-colored default symbol. Shared
          RewardImage reads the merchant from Redux, so no merchant props thread
          here. Wrapped so the fixed-size visual never shrinks in the flex row. */}
      <div className={css({ flexShrink: 0 })}>
        <RewardImage
          rewardIcon={reward.image_url ?? undefined}
          // Empty alt for a nameless reward (label is nullable on the wire) —
          // the visual is decorative here, since the name renders as text
          // immediately beside it, so a blank is correct rather than invented copy.
          alt={reward.label ?? ''}
          height={`${IMAGE_SIZE_PX}px`}
          width={`${IMAGE_SIZE_PX}px`}
        />
      </div>
      <div className={css({ display: 'flex', flexDirection: 'column' })}>
        <Text.BodyText4 bold>{reward.label}</Text.BodyText4>
        {reward.value_cents != null && (
          <Text.BodyText5 color="grey70">
            {t('valued_at', {
              amount: currencyFormatter(reward.value_cents / CENTS_PER_DOLLAR),
            })}
          </Text.BodyText5>
        )}
        {reward.points != null && (
          <Text.BodyText5 color="grey70">
            {t('points', { count: reward.points })}
          </Text.BodyText5>
        )}
      </div>
    </div>
  )

  // AI-1197: the chat transcript renders a reward differently from the
  // catalog grid — image-only when the reward has one, else the same neutral
  // outlined fallback the consumer ordering-ui RecoveryChat/ChatOfferCard
  // shows (and the Figma image-only design for the has-image case). Checked
  // directly against `reward.image_url` rather than going through the shared
  // RewardImage, whose 3-tier fallback (offer image → merchant icon → default
  // symbol) never reports "no image" — exactly the case this variant needs to
  // distinguish. Early-return rather than a nested ternary so the `<img>` is
  // only ever built inside its own truthy guard (no `?? undefined` needed).
  const renderCard = (): JSX.Element => {
    if (variant !== 'chat') return catalogCard
    if (!reward.image_url) return chatFallbackCard

    return (
      <img
        src={reward.image_url}
        alt={chatRewardName}
        className={css(CHAT_IMAGE_STYLE)}
      />
    )
  }

  const card = renderCard()

  // Early return so the default DOM is byte-identical — no extra wrapper.
  if (!showRewardLink) return card

  return (
    <div
      className={css({ display: 'flex', flexDirection: 'column', gap: '8px' })}
    >
      {card}
      {/* A PICTURE of the consumer app's CTA, not a merchant control.
          Deliberately non-interactive — no role, no tabIndex, no onClick —
          because the whole phone is inert in the static demo (R3).
            context: a focusable control that does nothing is a keyboard trap,
            not an affordance. Same category as the message bubbles: a preview of
            what the customer sees. ChevronRight is self-aria-hidden.
            context: brandColor as foreground can fail 4.5:1 for light brands
            (WCAG 1.4.3); deliberately accepted for this customer-view preview,
            the same documented tradeoff as the suggestion pills. It is passed as
            the `color` PROP, not via css() — uikit's Text resolves
            `theme.colors[color] || color`, so a raw brand hex is supported. */}
      <div
        className={css({ display: 'flex', alignItems: 'center', gap: '6px' })}
      >
        <Text.BodyText4 bold color={brandColor}>
          {t('view_reward')}
        </Text.BodyText4>
        <ChevronRight fillColor={brandColor} />
      </div>
    </div>
  )
}

export default SandboxRewardCard
